import { salesData, purchaseData, supplierData } from '../../data/dummyData';

interface InventoryStatus {
    totalProducts: number;
    lowStockItems: number;
    outOfStockItems: number;
    recentlyAdded: number;
}

interface SalesPerformance {
    totalSales: number;
    previousMonthSales: number;
    growth: number;
    topProducts: Array<{ name: string; sales: number }>;
}

interface BusinessTrend {
    trend: string;
    growth: number;
    category: string;
    insight: string;
}

export class AIResponseHandler {
    private static formatCurrency(amount: number): string {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(amount);
    }

    private static getInventoryStatus(): InventoryStatus {
        const products = new Set(salesData.flatMap(sale => sale.items.map(item => item.name)));
        const lowStockThreshold = 10;
        const productQuantities: { [key: string]: number } = {};

        salesData.forEach(sale => {
            sale.items.forEach(item => {
                if (!productQuantities[item.name]) {
                    productQuantities[item.name] = 0;
                }
                productQuantities[item.name] += item.quantity;
            });
        });

        const lowStock = Object.values(productQuantities).filter(qty => qty < lowStockThreshold).length;
        const outOfStock = Object.values(productQuantities).filter(qty => qty === 0).length;
        
        return {
            totalProducts: products.size,
            lowStockItems: lowStock,
            outOfStockItems: outOfStock,
            recentlyAdded: 5 // This would need to be tracked in the actual data
        };
    }

    private static getSalesPerformance(): SalesPerformance {
        const now = new Date();
        const currentMonth = now.getMonth();
        const currentYear = now.getFullYear();

        const thisMonthSales = salesData
            .filter(sale => {
                const saleDate = new Date(sale.date);
                return saleDate.getMonth() === currentMonth && saleDate.getFullYear() === currentYear;
            })
            .reduce((sum, sale) => sum + sale.totalAmount, 0);

        const lastMonthSales = salesData
            .filter(sale => {
                const saleDate = new Date(sale.date);
                return saleDate.getMonth() === (currentMonth - 1) && saleDate.getFullYear() === currentYear;
            })
            .reduce((sum, sale) => sum + sale.totalAmount, 0);

        const growth = lastMonthSales > 0 
            ? ((thisMonthSales - lastMonthSales) / lastMonthSales) * 100 
            : 0;

        const productSales: { [key: string]: number } = {};
        salesData.forEach(sale => {
            sale.items.forEach(item => {
                productSales[item.name] = (productSales[item.name] || 0) + item.quantity;
            });
        });

        const topProducts = Object.entries(productSales)
            .map(([name, sales]) => ({ name, sales }))
            .sort((a, b) => b.sales - a.sales)
            .slice(0, 5);

        return {
            totalSales: thisMonthSales,
            previousMonthSales: lastMonthSales,
            growth,
            topProducts
        };
    }

    private static getBusinessTrends(): BusinessTrend[] {
        const categories = new Set(salesData.flatMap(sale => sale.items.map(item => item.name.split(' ')[0])));
        const trends: BusinessTrend[] = [];

        categories.forEach(category => {
            const categoryItems = salesData.flatMap(sale => 
                sale.items.filter(item => item.name.startsWith(category))
            );

            const totalSales = categoryItems.reduce((sum, item) => sum + item.total, 0);
            const averagePrice = totalSales / categoryItems.length;
            const growth = Math.random() * 20 - 5; // This would need real historical data

            trends.push({
                category,
                trend: growth > 10 ? "Rising" : growth > 0 ? "Stable" : "Declining",
                growth: Number(growth.toFixed(1)),
                insight: this.generateInsight(growth, averagePrice)
            });
        });

        return trends.sort((a, b) => b.growth - a.growth);
    }

    private static generateInsight(growth: number, averagePrice: number): string {
        if (growth > 10) {
            return `Strong demand with high profit margins`;
        } else if (growth > 0) {
            return `Steady performance with consistent sales`;
        } else {
            return `May need price optimization or promotion`;
        }
    }

    private static generateSalesForecast(): string {
        const performance = this.getSalesPerformance();
        const projectedGrowth = performance.growth > 0 ? performance.growth : 5;
        const nextMonthSales = performance.totalSales * (1 + (projectedGrowth / 100));
        const quarterEndSales = nextMonthSales * 3;

        const trends = this.getBusinessTrends();
        const growthDrivers = trends
            .filter(t => t.growth > 0)
            .slice(0, 2)
            .map(t => t.category)
            .join(' and ');

        return "Based on current trends and historical data, we project:\n" +
               `- Next month sales: ${this.formatCurrency(nextMonthSales)} (Expected growth: ${projectedGrowth.toFixed(1)}%)\n` +
               `- Quarter end target: ${this.formatCurrency(quarterEndSales)}\n` +
               `- Key growth drivers: ${growthDrivers}`;
    }

    public static generateResponse(query: string): string {
        const lowerQuery = query.toLowerCase();

        if (lowerQuery.includes('inventory status')) {
            const status = this.getInventoryStatus();
            return `Current Inventory Status:\n` +
                   `- Total Products: ${status.totalProducts}\n` +
                   `- Low Stock Items: ${status.lowStockItems}\n` +
                   `- Out of Stock: ${status.outOfStockItems}\n` +
                   `- Recently Added: ${status.recentlyAdded}`;
        }

        if (lowerQuery.includes('sales performance')) {
            const performance = this.getSalesPerformance();
            return `Sales Performance This Month:\n` +
                   `- Total Sales: ${this.formatCurrency(performance.totalSales)}\n` +
                   `- Growth: ${performance.growth.toFixed(1)}% vs Last Month\n` +
                   `- Top Products:\n` +
                   performance.topProducts.map(p => `  • ${p.name}: ${p.sales} units`).join('\n');
        }

        if (lowerQuery.includes('trends')) {
            const trends = this.getBusinessTrends();
            return `Emerging Business Trends:\n` +
                   trends.map(t => 
                       `- ${t.category} (${t.trend})\n` +
                       `  • Growth: ${t.growth}%\n` +
                       `  • Insight: ${t.insight}`
                   ).join('\n\n');
        }

        if (lowerQuery.includes('forecast')) {
            return this.generateSalesForecast();
        }

        if (lowerQuery.includes('supplier') || lowerQuery.includes('vendors')) {
            const activeSuppliers = supplierData.filter(s => s.status === 'Active').length;
            const totalSpent = supplierData.reduce((sum, s) => sum + s.totalSpent, 0);
            const topSupplier = supplierData.reduce((max, s) => s.totalOrders > max.totalOrders ? s : max);

            return `Supplier Analysis:\n` +
                   `- Active Suppliers: ${activeSuppliers}\n` +
                   `- Total Spent: ${this.formatCurrency(totalSpent)}\n` +
                   `- Top Supplier: ${topSupplier.name}\n` +
                   `  • Orders: ${topSupplier.totalOrders}\n` +
                   `  • Spent: ${this.formatCurrency(topSupplier.totalSpent)}\n` +
                   `  • Rating: ${topSupplier.rating}/5`;
        }

        return "I'm not sure how to help with that query. Try asking about:\n" +
               "- Inventory status\n" +
               "- Sales performance\n" +
               "- Business trends\n" +
               "- Sales forecasts\n" +
               "- Supplier insights";
    }
} 